import { useState } from "react"
import { Clipboard, Info, KeyRound } from "lucide-react"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { editorCardClass, editorDescriptionClass, editorHeadingClass, editorInputClass } from "@/components/editor/editor-styles"
import type { ChatbotSettings } from "@/lib/types"

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("pt-BR", { month: "long", day: "numeric", year: "numeric" }).format(date)
}
function formatTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(date)
}

export function GeneralSection({ value, onChange }: { value: ChatbotSettings; onChange: (next: ChatbotSettings) => void }) {
  const [copied, setCopied] = useState(false)
  const active = value.status === "active"
  const set = <K extends keyof ChatbotSettings>(key: K, next: ChatbotSettings[K]) => onChange({ ...value, [key]: next })

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(value.publicKey)
      setCopied(true)
      toast.success("Chave pública copiada.")
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      toast.error("Não foi possível copiar a chave. Selecione manualmente.")
    }
  }

  return (
    <form className="space-y-5" onSubmit={(event) => event.preventDefault()}>
      {/* Assistant Identity */}
      <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Identidade do assistente</h2>
          <p className={editorDescriptionClass}>Esses dados são usados internamente em todo o seu workspace.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="internal-name" className="mb-1.5 block text-xs font-medium text-foreground">Nome interno do chatbot</Label>
            <Input id="internal-name" value={value.name} onChange={(event) => set("name", event.target.value)} className={editorInputClass} required />
          </div>
          <div>
            <Label htmlFor="company-name" className="mb-1.5 block text-xs font-medium text-foreground">Nome da empresa</Label>
            <Input id="company-name" value={value.companyName} onChange={(event) => set("companyName", event.target.value)} className={editorInputClass} required />
          </div>
        </div>
      </section>

      {/* Public Key */}
      <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Chave pública</h2>
          <p className={editorDescriptionClass}>Use esta chave ao conectar o widget do assistente ao seu site.</p>
        </div>
        <div className="field-bg flex min-h-11 items-center gap-3 rounded-xl border border-border px-3">
          <KeyRound className="muted shrink-0 size-4" />
          <code className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">{value.publicKey}</code>
          <button type="button" onClick={() => void copyKey()} className="muted flex h-9 shrink-0 items-center gap-2 rounded-lg px-2 text-xs font-semibold transition hover:bg-accent hover:text-accent-foreground">
            <Clipboard className="size-3.5" />
            <span className="hidden sm:inline">{copied ? "Copiado" : "Copiar"}</span>
          </button>
        </div>
        <p className="muted mt-2 flex items-center gap-1.5 text-[11px]"><Info className="size-3.5" />Esta chave é segura para uso no código de instalação do lado do cliente.</p>
      </section>

      {/* Assistant Availability */}
      <section className={editorCardClass}>
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <h2 className={editorHeadingClass}>Disponibilidade em todos os sites</h2>
            <p className={editorDescriptionClass}>Pause a publicação em todos os domínios. Conversas públicas recebem um aviso e novas mensagens ficam bloqueadas. A área de teste continua disponível. Para pausar apenas um site, use Instalação → Domínios.</p>
          </div>
          <Switch checked={active} onCheckedChange={(checked) => set("status", checked ? "active" : "paused")} aria-label="Alternar disponibilidade em todos os sites" />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3.5">
          <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${active ? "border-success/20 bg-success/10 text-success" : "border-warning/20 bg-warning/10 text-warning"}`}>
            <span className={`size-1.5 rounded-full ${active ? "bg-success" : "bg-warning"}`} />
            {active ? "Ativo" : "Pausado"}
          </span>
          <span className="muted text-[11px]">Última alteração de status às {formatTime(value.updatedAt)}</span>
        </div>
      </section>

      {/* Operational Information */}
      <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Informações operacionais</h2>
          <p className={editorDescriptionClass}>Um resumo rápido do estado atual deste assistente.</p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <p className="muted text-[10px] uppercase tracking-wider font-semibold">Criado em</p>
            <p className="mt-1 text-[14px] font-semibold text-foreground">{formatDate(value.createdAt)}</p>
          </div>
          <div>
            <p className="muted text-[10px] uppercase tracking-wider font-semibold">Última atualização</p>
            <p className="mt-1 text-[14px] font-semibold text-foreground">{formatDate(value.updatedAt)}</p>
          </div>
          <div>
            <p className="muted text-[10px] uppercase tracking-wider font-semibold">Nome do assistente (IA)</p>
            <p className="mt-1 text-[14px] font-semibold text-foreground">{value.assistantName || "—"}</p>
            <p className="muted mt-1 text-[11px]">Editado em Aparência &gt; Perfil. Título do cabeçalho é separado.</p>
          </div>
          <div>
            <p className="muted text-[10px] uppercase tracking-wider font-semibold">Status do sistema</p>
            <span className={`mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ${active ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
              <span className={`size-1.5 rounded-full ${active ? "bg-success" : "bg-warning"}`} />
              {active ? "Operacional" : "Pausado"}
            </span>
          </div>
        </div>
      </section>
    </form>
  )
}
